// @flow

import React, { type Node } from 'react';
import { Grid } from './grid';
import createMuiSpacingRules from '../../birch/layouts/mui_spacing';
import styled from 'styled-components';

// There exists a Stack within Material UI v5, but we are currently behind on this (also due to React 16).
// We aim to have a similar API for an eventual switch over.
// References:
// https://mui.com/components/stack/
// https://mui.com/api/stack/

type Props = {
  /**
   * The content of the component.
   */
  children?: Node,
  /**
   * Defines the `flex-direction` style property.
   * It is applied for all screen sizes.
   * @default 'column'
   */
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse',
  /**
   * Defines the space between immediate children.
   * @default 0
   */
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10,
  /**
   * Add an element between each child.
   */
  divider?: React$Element<any>,
  /**
   * Sets align-content flexbox CSS value
   */
  alignContent?:
    | 'stretch'
    | 'center'
    | 'flex-start'
    | 'flex-end'
    | 'space-between'
    | 'space-around',
  /**
   * Sets align-items flexbox CSS value
   */
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline',
  /**
   * Sets justify-content flexbox CSS value
   */
  justify?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly',
  /**
   * Custom style object (padding/margin styles only)
   */
  sx?: any,
  /**
   * Determines how overflow is wrapped.
   */
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse',
};

const DEFAULT_DIRECTION = 'column';

// This value is multiplied by 8px and results in a 24px gutter between elements
const DEFAULT_SPACING = 3;

// Dump the parsed rules into the styled component
/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const StyledGrid = styled(Grid)`
  ${({ sx }) => sx}
`;

const Stack = ({ direction, children, divider, sx, ...rest }: Props) => {
  // converting React.Children into an array filters out nulls
  const childrenArray = React.Children.toArray(children);

  return (
    <StyledGrid
      container
      direction={direction || DEFAULT_DIRECTION}
      spacing={DEFAULT_SPACING}
      data-provides="stack"
      {...rest}
      sx={createMuiSpacingRules(sx)}
    >
      {childrenArray &&
        childrenArray.map((child, childNum) => {
          const isLast = childNum === childrenArray.length - 1;
          return (
            <React.Fragment key={childNum}>
              <Grid item>{child}</Grid>
              {divider && !isLast && (
                <Grid item role="separator">
                  {divider}
                </Grid>
              )}
            </React.Fragment>
          );
        })}
    </StyledGrid>
  );
};

export { Stack };
